import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { auditService } from '../../services/auditService';
import { useCatalog } from '../../lib/permissions';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';
import AuditLogTable from './AuditLogTable';

const PAGE_SIZE = 15;

const ClientActivity = ({ clientId }) => {
    const { catalog } = useCatalog();
    const [page, setPage] = useState(0);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await auditService.getEntityTrail('Client', clientId, page, PAGE_SIZE);
            setResult(res?.data || null);
        } catch (err) {
            setError(err.message || 'Failed to load activity');
        } finally {
            setLoading(false);
        }
    }, [clientId, page]);

    useEffect(() => { load(); }, [load]);

    const rows = result?.content || [];
    const totalPages = result?.totalPages || 0;

    if (loading && !result) return <LoadingSpinner message="Loading activity..." />;

    return (
        <div className="space-y-4">
            {error && <ErrorAlert error={error} onRetry={load} />}
            {rows.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-white py-12 text-center">
                    <ScrollText className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                    <p className="text-sm text-gray-500">No activity recorded for this client yet.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <AuditLogTable rows={rows} catalog={catalog} compact />
                    {totalPages > 1 && (
                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
                            <button
                                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                                disabled={page === 0 || loading}
                                className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50 disabled:opacity-40"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <span>Page {page + 1} of {totalPages}</span>
                            <button
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page + 1 >= totalPages || loading}
                                className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50 disabled:opacity-40"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientActivity;
